import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert, Badge, InputGroup } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

const Competitors = () => {
  const { user } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    lenderName: '',
    aliases: [''],
    active: true,
    priority: 0,
    notes: ''
  });

  // Load competitors on mount
  useEffect(() => {
    loadCompetitors();
  }, []);

  const loadCompetitors = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/bank-parsing/competitors');
      if (!response.ok) throw new Error('Failed to load competitors');
      
      const data = await response.json();
      setCompetitors(data.competitors || []);
    } catch (error) {
      console.error('Error loading competitors:', error);
      notifyError('Failed to load competitors');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (competitor = null) => {
    if (competitor) {
      setEditingCompetitor(competitor);
      setFormData({
        lenderName: competitor.lenderName,
        aliases: competitor.aliases.length > 0 ? competitor.aliases : [''],
        active: competitor.active,
        priority: competitor.priority || 0,
        notes: competitor.notes || ''
      });
    } else {
      setEditingCompetitor(null);
      setFormData({
        lenderName: '',
        aliases: [''],
        active: true,
        priority: 0,
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCompetitor(null);
    setFormData({
      lenderName: '',
      aliases: [''],
      active: true,
      priority: 0,
      notes: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAliasChange = (index, value) => {
    const newAliases = [...formData.aliases];
    newAliases[index] = value;
    setFormData(prev => ({
      ...prev,
      aliases: newAliases
    }));
  };

  const addAlias = () => {
    setFormData(prev => ({
      ...prev,
      aliases: [...prev.aliases, '']
    }));
  };

  const removeAlias = (index) => {
    if (formData.aliases.length > 1) {
      const newAliases = formData.aliases.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        aliases: newAliases
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.lenderName.trim()) {
      notifyError('Lender name is required');
      return;
    }
    
    const cleanAliases = formData.aliases.filter(alias => alias.trim() !== '');
    if (cleanAliases.length === 0) {
      notifyError('At least one alias is required');
      return;
    }

    try {
      const url = editingCompetitor 
        ? `/api/bank-parsing/competitors/${editingCompetitor._id}`
        : '/api/bank-parsing/competitors';
      
      const method = editingCompetitor ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lenderName: formData.lenderName.trim(),
          aliases: cleanAliases,
          active: formData.active,
          priority: parseInt(formData.priority) || 0,
          notes: formData.notes.trim()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save competitor');
      }

      notifySuccess(editingCompetitor ? 'Competitor updated successfully' : 'Competitor created successfully');
      handleCloseModal();
      loadCompetitors();
    } catch (error) {
      console.error('Error saving competitor:', error);
      notifyError(error.message);
    }
  };

  const handleDelete = async (competitor) => {
    if (!window.confirm(`Are you sure you want to delete "${competitor.lenderName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/bank-parsing/competitors/${competitor._id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete competitor');
      }

      notifySuccess('Competitor deleted successfully');
      loadCompetitors();
    } catch (error) {
      console.error('Error deleting competitor:', error);
      notifyError(error.message);
    }
  };

  const toggleActive = async (competitor) => {
    try {
      const response = await fetch(`/api/bank-parsing/competitors/${competitor._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...competitor,
          active: !competitor.active
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update competitor');
      }

      notifySuccess(`Competitor ${competitor.active ? 'deactivated' : 'activated'} successfully`);
      loadCompetitors();
    } catch (error) {
      console.error('Error updating competitor:', error);
      notifyError(error.message);
    }
  };

  // Filter competitors based on search term
  const filteredCompetitors = competitors.filter(competitor => 
    competitor.lenderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    competitor.aliases.some(alias => alias.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Container fluid className="py-4" style={{ maxWidth: '1600px' }}>
      <Row>
        <Col className="px-3 px-md-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>Competitor Management</h2>
              <p className="text-muted">Manage known competitors and their aliases for bank statement analysis</p>
            </div>
            <Button variant="primary" onClick={() => handleShowModal()}>
              Add New Competitor
            </Button>
          </div>

          <Card>
            <Card.Header>
              <Row className="align-items-center">
                <Col md={6}>
                  <h5 className="mb-0">Known Competitors ({filteredCompetitors.length})</h5>
                </Col>
                <Col md={6}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search competitors or aliases..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : filteredCompetitors.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">
                    {searchTerm ? 'No competitors match your search' : 'No competitors found'}
                  </p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '200px' }}>Lender Name</th>
                      <th style={{ minWidth: '250px' }}>Aliases</th>
                      <th style={{ minWidth: '80px' }}>Status</th>
                      <th style={{ minWidth: '80px' }}>Priority</th>
                      <th style={{ minWidth: '220px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompetitors.map((competitor) => (
                      <tr key={competitor._id}>
                        <td>
                          <div>
                            <strong>{competitor.lenderName}</strong>
                            {competitor.notes && (
                              <div>
                                <small className="text-muted">{competitor.notes}</small>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {competitor.aliases.map((alias, idx) => (
                              <Badge key={idx} bg="secondary" className="small">
                                {alias}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td>
                          <Badge bg={competitor.active ? 'success' : 'danger'}>
                            {competitor.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>{competitor.priority || 0}</td>
                        <td>
                          <div className="d-flex gap-1 flex-nowrap">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowModal(competitor)}
                              className="px-2"
                            >
                              Edit
                            </Button>
                            <Button
                              variant={competitor.active ? 'outline-warning' : 'outline-success'}
                              size="sm"
                              onClick={() => toggleActive(competitor)}
                              className="px-2"
                              title={competitor.active ? 'Deactivate' : 'Activate'}
                            >
                              {competitor.active ? 'Off' : 'On'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(competitor)}
                              className="px-2"
                            >
                              Del
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCompetitor ? 'Edit Competitor' : 'Add New Competitor'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Lender Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lenderName}
                    onChange={(e) => handleInputChange('lenderName', e.target.value)}
                    placeholder="e.g., OnDeck Capital"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    placeholder="0"
                  />
                  <Form.Text className="text-muted">Higher priority = matched first</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Aliases *</Form.Label>
              <Form.Text className="text-muted d-block mb-2">
                Add variations of how this lender appears in bank statements
              </Form.Text>
              {formData.aliases.map((alias, index) => (
                <div key={index} className="d-flex gap-2 mb-2">
                  <Form.Control
                    type="text"
                    value={alias}
                    onChange={(e) => handleAliasChange(index, e.target.value)}
                    placeholder="e.g., ONDECK, OnDeck Capital, ONDECK CAPITAL"
                  />
                  {formData.aliases.length > 1 && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeAlias(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline-secondary" size="sm" onClick={addAlias}>
                Add Another Alias
              </Button>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Optional notes about this competitor"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="active-checkbox"
                label="Active (include in competitor detection)"
                checked={formData.active}
                onChange={(e) => handleInputChange('active', e.target.checked)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCompetitor ? 'Update Competitor' : 'Add Competitor'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Competitors;
